import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { FileText, ArrowRight, Storefront, Copy } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import TOTPWidget from '../components/TOTPWidget';
import LoginPasswordWidget from '../components/LoginPasswordWidget';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const copy = (txt, label) => {
  if (!txt) return;
  navigator.clipboard.writeText(txt);
  toast.success(`${label} copied`);
};

// Smart key detection — handle bulk-uploaded weird keys + standard keys
function pickAccountFields(data) {
  const d = data || {};
  const isAdminOnly = (k) => /(linkedgmail|recoveryemail|createdby)/i.test((k || '').toLowerCase().replace(/\s/g, ''));
  const keys = Object.keys(d).filter(k => !isAdminOnly(k));
  const find = (...patterns) => {
    for (const p of patterns) {
      const k = keys.find(k => k.toLowerCase().replace(/[^a-z0-9]/g, '').includes(p));
      if (k) return k;
    }
    return null;
  };
  const isEmail = v => /^[\w.+-]+@[\w-]+\.[\w.-]+$/i.test(String(v || '').trim());
  const isPin = v => /^\d{4,8}$/.test(String(v || '').trim());
  const isMobile = v => /^(\+?91)?[6-9]\d{9}$/.test(String(v || '').replace(/\s|-/g, ''));
  const isBase32 = v => { const s = String(v || '').replace(/\s/g, '').toUpperCase(); return s.length >= 16 && /^[A-Z2-7=]+$/.test(s); };

  let emailKey = find('acount', 'account', 'email');
  let pwKey = keys.find(k => /^(pasword|password|pswd)/i.test(k.replace(/[^a-z0-9]/gi, '')));
  let pw2Key = keys.find(k => k !== pwKey && /^(pasword|password|pswd)/i.test(k.replace(/[^a-z0-9]/gi, '')));
  let mobKey = find('mobile', 'phone', 'contact');
  let otpKey = find('totpsecret', 'otpcod', 'otpcode', 'totp', 'otp', 'secret');

  // Value-pattern fallback for live-env weird keys
  const claimed = new Set([emailKey, pwKey, pw2Key, mobKey, otpKey].filter(Boolean));
  if (!emailKey) emailKey = keys.find(k => !claimed.has(k) && (isEmail(d[k]) || isEmail(k)));
  if (emailKey) claimed.add(emailKey);
  if (!otpKey) otpKey = keys.find(k => !claimed.has(k) && (isBase32(d[k]) || isBase32(k)));
  if (otpKey) claimed.add(otpKey);
  if (!mobKey) mobKey = keys.find(k => !claimed.has(k) && (isMobile(d[k]) || isMobile(k)));
  if (mobKey) claimed.add(mobKey);
  if (!pwKey) pwKey = keys.find(k => !claimed.has(k) && (isPin(d[k]) || isPin(k)));
  if (pwKey) claimed.add(pwKey);

  const resolve = (k, classifier) => {
    if (!k) return '';
    const v = d[k]; const kLook = classifier(k); const vLook = classifier(v);
    if (kLook && !vLook) return k;
    if (!kLook && vLook) return v || '';
    if (kLook && vLook) return v || k;
    return v || '';
  };
  return {
    email: resolve(emailKey, isEmail),
    mobile: resolve(mobKey, isMobile),
    password: resolve(pwKey, isPin),
    password2: pw2Key ? (d[pw2Key] || pw2Key) : '',
    secret: resolve(otpKey, isBase32),
    emailKey, pwKey, pw2Key, mobKey, otpKey,
  };
}

export default function MyBookingWorkspacePage() {
  const [files, setFiles] = useState([]);
  const [account, setAccount] = useState(null);
  const [card, setCard] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, aRes, cRes, sRes] = await Promise.all([
        api.get('/booking-files'),
        api.get('/booking-pool/active-account?platform=any'),
        api.get('/booking-pool/cards'),
        api.get('/sub-bookings/my-today-stats').catch(() => ({ data: null })),
      ]);
      const list = Array.isArray(fRes.data) ? fRes.data : (fRes.data?.items || []);
      const active = list.filter(f => ['processing', 'assigned'].includes(f.status));
      setFiles(active);
      setAccount(aRes.data || null);
      const firstCard = (cRes.data || []).find(x => !x.in_cooldown && x.available > 0 && (x.available_limit === undefined || x.available_limit > 0));
      setCard(firstCard || (cRes.data || [])[0] || null);
      setStats(sRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acc = account ? pickAccountFields(account.data) : null;

  return (
    <div className="space-y-4" data-testid="my-booking-workspace">
      <div>
        <h1 className="text-2xl font-bold">My Booking Workspace</h1>
        <p className="text-sm text-gray-500">Aapko jo bookings assign hui hain — sab ek jagah. Account credentials + card auto-load ho jayenge.</p>
      </div>

      {/* Today's stats — gamification widget */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="my-today-stats">
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <div className="text-[10px] uppercase font-bold text-blue-700">📊 Today Total</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-[10px] uppercase font-bold text-emerald-700">✅ Success</div>
            <div className="text-2xl font-bold text-emerald-700">{stats.success}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-[10px] uppercase font-bold text-amber-700">⏳ Pending</div>
            <div className="text-2xl font-bold text-amber-700">{stats.pending + (stats.draft || 0)}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded p-3">
            <div className="text-[10px] uppercase font-bold text-rose-700">❌ Failed</div>
            <div className="text-2xl font-bold text-rose-700">{stats.failed}</div>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded p-3">
            <div className="text-[10px] uppercase font-bold text-violet-700">🎯 Success Rate</div>
            <div className="text-2xl font-bold text-violet-700">{stats.success_rate}%</div>
            {stats.success_amount > 0 && (
              <div className="text-[10px] text-violet-600">{fmt(stats.success_amount)} done</div>
            )}
          </div>
        </div>
      )}

      {/* Account + Card credentials banner */}
      {account && acc && (
        <div className="bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200 rounded p-3" data-testid="my-active-account">
          <div className="text-[11px] font-bold text-blue-900 mb-2 inline-flex items-center gap-1">
            <Storefront size={13} weight="fill" /> ACTIVE ACCOUNT — {account.platform?.toUpperCase()} · {account.daily_usage_count}/{account.daily_limit} used
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            <div onClick={() => copy(acc.email, 'Email')} className="cursor-pointer hover:bg-white/60 p-1 rounded">
              <div className="text-[10px] text-gray-500 uppercase">{acc.emailKey || 'Email'}</div>
              <div className="font-mono truncate">{acc.email || '-'} <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(acc.mobile, 'Mobile')} className="cursor-pointer hover:bg-white/60 p-1 rounded">
              <div className="text-[10px] text-gray-500 uppercase">{acc.mobKey || 'Mobile'}</div>
              <div className="font-mono">{acc.mobile || '-'} <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(acc.password, 'Password')} className="cursor-pointer hover:bg-white/60 p-1 rounded hidden">
              <div className="text-[10px] text-gray-500 uppercase">{acc.pwKey || 'Password'}</div>
              <div className="font-mono">{acc.password || '-'} <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            {acc.password2 && (
              <div onClick={() => copy(acc.password2, 'Password2')} className="cursor-pointer hover:bg-white/60 p-1 rounded hidden">
                <div className="text-[10px] text-gray-500 uppercase">{acc.pw2Key}</div>
                <div className="font-mono break-all">{acc.password2} <Copy size={10} className="inline text-gray-400" /></div>
              </div>
            )}
            <div className="p-1 hidden">
              <div className="text-[10px] text-gray-500 uppercase">TOTP</div>
              <TOTPWidget secret={acc.secret} label="OTP" />
            </div>
            <div className="p-1">
              <div className="text-[10px] text-gray-500 uppercase">🔑 Login</div>
              <div className="space-y-1">
                {acc.password && (
                  <LoginPasswordWidget password={acc.password} secret={acc.secret} label="Pwd+OTP" testId="my-login-pwd" />
                )}
                {acc.password2 && (
                  <LoginPasswordWidget password={acc.password2} secret={acc.secret} label="Pwd2+OTP" testId="my-login-pwd2" />
                )}
              </div>
            </div>
          </div>
          {card && (
            <div className="mt-2 pt-2 border-t border-blue-200 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[10px] text-gray-500 font-bold">💳 CARD:</span>
              <span className="font-mono">**** {card.last4 || '----'}</span>
              {card.card_holder && <span className="text-gray-500">· {card.card_holder}</span>}
              <span className="text-gray-400">·</span>
              <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold" data-testid="card-balance-badge">
                Balance: {fmt(card.available_limit || 0)}
              </span>
              {card.initial_limit > 0 && card.initial_limit !== card.available_limit && (
                <span className="text-[10px] text-gray-500">
                  (used {fmt(card.used_amount || 0)} / {fmt(card.initial_limit)})
                </span>
              )}
              <span className="text-gray-500 ml-auto">{card.daily_usage_count}/{card.daily_limit} txns today</span>
            </div>
          )}
        </div>
      )}

      {/* Assigned files list */}
      <div>
        <div className="text-sm font-bold mb-2 inline-flex items-center gap-1">
          <FileText size={14} /> Aapke Assigned Files ({files.length})
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : files.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400 bg-gray-50 rounded border" data-testid="empty-files">
            Abhi koi file assign nahi hui. Staff se booking distribute hone ka wait karo.
          </div>
        ) : (
          <div className="space-y-2">
            {files.map(f => (
              <div key={f.id} className="bg-white border rounded p-3 flex items-center gap-3 hover:shadow-md transition-shadow" data-testid={`file-${f.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {f.original_filename || f.distributor_name || '(Unnamed file)'}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      f.status === 'processing' ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'
                    }`}>{f.status}</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {f.distributor_name && <span>📦 {f.distributor_name}</span>}
                    {f.oil_company && <span className="ml-2">⛽ {f.oil_company}</span>}
                    {f.total_bookings_count > 0 && <span className="ml-2">📋 {f.total_bookings_count} bookings</span>}
                    {f.assignment_instruction && <span className="ml-2 text-amber-700">📝 {f.assignment_instruction}</span>}
                  </div>
                  {f.amount_per_unit && (
                    <div className="text-[11px] text-gray-600 mt-0.5">Rate: {fmt(f.amount_per_unit)}/cyl</div>
                  )}
                </div>
                <Link to={`/booking-workspace/${f.id}`}>
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1" data-testid={`open-${f.id}`}>
                    Open Workspace <ArrowRight size={14} />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[11px] text-gray-500">
        💡 "Open Workspace" pe click karo — wahan saare distributed rows + Group-wise account+card auto-rotate hota hai. Ek hi screen me booking complete karo.
      </div>
    </div>
  );
}
